import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Users, BarChart3 } from 'lucide-react';
import { trackFormSubmit } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function IndustryDeck() {
  const { industry } = useParams();
  const [deck, setDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ brand_name: '', contact_name: '', email: '', industry: industry || '', product_name: '', budget_range: '', target_exec_description: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchDeck();
  }, [industry]);

  const fetchDeck = async () => {
    try {
      const { data } = await axios.get(`${API}/decks/${industry}`);
      setDeck(data);
      setFormData(f => ({ ...f, industry: industry }));
    } catch {}
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/campaign-intake`, formData);
      setSubmitted(true);
      trackFormSubmit('campaign_intake_industry_deck', {
        industry: formData.industry || 'unspecified',
        budget_range: formData.budget_range || 'unspecified',
      });
    } catch {}
  };

  if (loading) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF4343] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!deck) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <div className="text-center">
        <p className="text-[#9CA3AF] mb-4">Industry deck not found.</p>
        <Link to="/for-brands" className="text-[#FF4343] text-sm hover:underline">Back to For Brands</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#090E33]" data-testid="industry-deck">
      {/* Hero */}
      <section className="py-20 sm:py-28 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link to="/for-brands" className="flex items-center gap-2 text-[#9CA3AF] hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <span className="text-xs font-mono uppercase tracking-wider text-[#FF4343] mb-4 block">{industry} Vertical</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="deck-headline">
            {deck.headline}
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-3xl">{deck.subheadline}</p>
        </div>
      </section>

      {/* Executive Examples */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Executives in Our <span className="text-[#FF4343]">Network</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {deck.exec_examples?.map((ex, i) => (
              <div key={i} className="bg-[#111827] border border-white/10 p-6 text-center">
                <Users className="w-8 h-8 text-[#FF4343] mx-auto mb-3" />
                <span className="text-sm text-white font-medium">{ex}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campaign Examples */}
      <section className="py-16 bg-[#111827]/30 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Active <span className="text-[#FF4343]">Campaign Types</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {deck.campaign_examples?.map((ce, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#111827] border border-white/10 p-4">
                <BarChart3 className="w-5 h-5 text-[#FF4343] flex-shrink-0" />
                <span className="text-sm text-[#9CA3AF]">{ce}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      {deck.case_studies?.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Results in <span className="text-[#FF4343]">{industry}</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {deck.case_studies.map(cs => (
                <div key={cs.id} className="bg-[#111827] border border-white/10 p-5 sm:p-8 border-l-[3px] border-l-[#FF4343]">
                  <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4">
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-[#FF4343] tabular-nums leading-tight truncate">${cs.total_spend?.toLocaleString()}</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">Spend</p></div>
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-white tabular-nums leading-tight truncate">{(cs.total_impressions / 1000).toFixed(0)}K</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">Impressions</p></div>
                    <div className="min-w-0"><span className="block font-mono text-base sm:text-2xl font-bold text-[#10B981] tabular-nums leading-tight truncate">${cs.cpm}</span><p className="text-[10px] sm:text-xs text-[#6B7280] mt-1">CPM</p></div>
                  </div>
                  <p className="text-sm text-[#9CA3AF] italic">"{cs.outcome_quote}"</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {deck.testimonial?.name && deck.testimonial.name !== 'Coming Soon' && (
        <section className="py-16 bg-[#111827]/30 border-y border-white/5">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <p className="font-display text-2xl italic text-white mb-6">"{deck.testimonial.quote}"</p>
            <p className="text-[#FF4343] font-medium">{deck.testimonial.name}</p>
            <p className="text-[#6B7280] text-sm">{deck.testimonial.title}</p>
          </div>
        </section>
      )}

      {/* Intake Form */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl font-bold text-center mb-8">Get <span className="text-[#FF4343]">Started</span></h2>
          {submitted ? (
            <div className="text-center py-8 bg-[#111827] border border-white/10 p-8" data-testid="deck-intake-success">
              <p className="text-[#10B981] font-semibold text-lg mb-2">Thank you!</p>
              <p className="text-[#9CA3AF] text-sm">We'll respond within 1 business day.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#111827] border border-white/10 p-8 space-y-4" data-testid="deck-intake-form">
              <input placeholder="Brand Name" required value={formData.brand_name} onChange={e => setFormData({...formData, brand_name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              <input placeholder="Contact Name" required value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              <input type="email" placeholder="Email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              <input placeholder="Product Name" required value={formData.product_name} onChange={e => setFormData({...formData, product_name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-[#6B7280] text-sm focus:outline-none focus:border-[#FF4343]" />
              <select value={formData.budget_range} onChange={e => setFormData({...formData, budget_range: e.target.value})} required
                className="w-full px-4 py-3 bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
                <option value="">Monthly Budget</option>
                {['$1,000 - $5,000', '$5,000 - $15,000', '$15,000 - $50,000', '$50,000+'].map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <button type="submit" data-testid="deck-intake-submit"
                className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] py-3 text-sm font-semibold transition-all duration-300">
                Request Campaign Proposal
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Powered by Freddy Media */}
      <section className="py-8 sm:py-14 bg-white" data-testid="powered-by-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/lnrtssp0_freddy-logo-script.png" alt="Freddy Media" className="h-10 sm:h-14" />
              <div className="h-10 w-px bg-[#090E33]/10 hidden sm:block" />
              <div>
                <p className="text-[#090E33] text-sm font-semibold tracking-wide">Powered by Freddy Media</p>
                <p className="text-[#090E33]/50 text-xs mt-0.5">The Executive Endorsement Company</p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/0razqlkv_freddy-logo-seal-symbol.png" alt="" className="h-11 w-11 opacity-80" />
              <Link to="/for-brands" data-testid="powered-by-cta"
                className="border-2 border-[#090E33] bg-transparent text-[#090E33] hover:bg-[#090E33] hover:text-white px-6 py-2.5 text-sm font-semibold transition-all duration-300">
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
